import React from "react";
import { Sparkles, Plus, Folder, LayoutDashboard, Users, X, MessagesSquare, LifeBuoy, Key, StickyNote, CalendarDays, Megaphone } from "lucide-react";
import Logo from "./Logo";

const Sidebar = ({ openNewCaseModal, onNavigate, currentView, isOpen, onClose, featureFlags = {}, unreadChatCount = 0 }) => {
  const navItems = [
    { id: "cases", label: "Cases", icon: Folder, requiresFeature: null },
    { id: "lender-dashboard", label: "Dashboard", icon: LayoutDashboard, requiresFeature: null },
    { id: "calendar", label: "Calendar", icon: CalendarDays, requiresFeature: "hasCalendar" },
    { id: "notes", label: "Notes", icon: StickyNote, requiresFeature: "hasNotes" },
    { id: "teams", label: "Teams", icon: Users, requiresFeature: "hasTeams" },
    { id: "chat", label: "Team Chat", icon: MessagesSquare, requiresFeature: "hasTeamChat" },
    { id: "assistant", label: "A.I Assistant", icon: Sparkles, requiresFeature: "hasCaseAssistant" },
    { id: "marketing", label: "Marketing", icon: Megaphone, requiresFeature: "hasSocialMedia" },
    { id: "developer", label: "Developer", icon: Key, requiresFeature: "hasApiAccess" },
    { id: "support", label: "Support", icon: LifeBuoy, requiresFeature: null }
  ];

  // Filter items based on feature flags
  const availableItems = navItems.filter(item => {
    if (!item.requiresFeature) return true;
    return featureFlags[item.requiresFeature] === true;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 dark:bg-slate-950/80 bg-slate-900/20 backdrop-blur-sm md:hidden transition-all"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 dark:bg-slate-900 bg-white border-r dark:border-slate-800 border-slate-200 flex flex-col transition-transform duration-300 ease-in-out
          md:translate-x-0 md:static md:dark:bg-slate-900/50 md:bg-white/50
          ${isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
        `}
      >
        <div className="p-6 flex items-center justify-between">
          <div className="bg-slate-900/90 dark:bg-transparent p-2 dark:p-0 rounded-lg backdrop-blur-sm shadow-sm dark:shadow-none">
            <Logo className="h-12 w-auto max-w-[10rem]" />
          </div>
          <button
            onClick={onClose}
            aria-label="close"
            className="md:hidden dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 mb-6">
          <button
            onClick={() => {
              openNewCaseModal();
              onClose?.();
            }}
            className="w-full py-2 px-4 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors shadow-lg shadow-yellow-900/20"
          >
            <Plus className="w-4 h-4" />
            New case
          </button>
        </div>

        <nav className="flex-1 px-2 space-y-1">
          <div className="px-3 py-2 text-xs font-semibold dark:text-slate-500 text-slate-400 uppercase tracking-wider">
            Workspace
          </div>
          {availableItems.map((item) => (
            <a
              key={item.id}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onNavigate(item.id);
                onClose?.();
              }}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg group transition-colors ${currentView === item.id
                ? "dark:text-slate-300 text-yellow-700 dark:bg-slate-800 bg-yellow-50"
                : "dark:text-slate-400 text-slate-600 dark:hover:bg-slate-800/50 hover:bg-slate-100/80"
                }`}
            >
              <item.icon className={`w-4 h-4 transition-colors ${currentView === item.id ? "text-yellow-600 dark:text-yellow-400" : "text-yellow-500/70 dark:text-yellow-400/70 group-hover:text-yellow-600 dark:group-hover:text-yellow-400"}`} />
              <span className="font-medium flex-1">{item.label}</span>
              {item.id === "chat" && unreadChatCount > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                  {unreadChatCount > 99 ? '99+' : unreadChatCount}
                </span>
              )}
            </a>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
